import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import DatePickerComponent from '../datePickerComponent';
import { useEffect, useState } from 'react';
import InfoRow from '../infoRow';
import Header from '../header';
import InputField from '../inputField';
import {
  fetchAjudArchiveData,
  updateAjudTransference,
  AjudArchiveData,
  ModalTransferProps,
  moneyMask,
} from './service';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxHeight: '90vh', // Limita a altura máxima do modal
  overflowY: 'auto', // Adiciona rolagem vertical quando necessário
  bgcolor: 'background.paper',
  border: '2px solid #cecece',
  borderRadius: 5,
  boxShadow: 24,
  pt: 2,
  px: 4,
  pb: 3,
};

const ModalTransfer = ({ open, ajudValue, closeModal }: ModalTransferProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [inputDisable, setInputDisable] = useState(false);
  const [data, setData] = useState<AjudArchiveData>(); // Estado para armazenar a resposta
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Obtendo os dados
  useEffect(() => {
    const getData = async () => {
      setIsLoading(true);
      const result = await fetchAjudArchiveData(ajudValue?.court_order || '');
      setData(result || undefined);
      setIsLoading(false);
    };

    getData();
  }, [ajudValue?.court_order]);

  const resetState = () => {
    setPage(0);
    setRowsPerPage(5);
  };

  const handleClose = (shouldReload?: boolean) => {
    resetState();
    closeModal(shouldReload);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const emptyRows =
    rowsPerPage -
    Math.min(rowsPerPage, data?.data.length || 0 - page * rowsPerPage);

  const formik = useFormik({
    initialValues: {
      response_code: ajudValue?.response_code || '',
      available_value_transference:
        ajudValue?.available_value_transference || '',
      lock_remaining: ajudValue?.lock_remaining || '',
      data_limit_transference: ajudValue?.data_limit_transference,
    },
    validationSchema: Yup.object({
      response_code: Yup.string().required('Código de resposta é obrigatório'),

      available_value_transference: Yup.string().when('response_code', {
        is: (code: string) => code !== '07' && code !== '47', // Verifica se não é 07 ou 47
        then: (schema) => schema.required('Valor disponível é obrigatório'),
      }),

      lock_remaining: Yup.string().when('response_code', {
        is: (code: string) => code !== '07' && code !== '47', // Verifica se não é 07 ou 47
        then: (schema) => schema.required('Valor restante é obrigatório'),
      }),

      data_limit_transference: Yup.string()
        .nullable() // Permitir que o valor seja null
        .when('response_code', {
          is: (code: string) => code !== '07' && code !== '47', // Verifica se não é 07 ou 47
          then: (schema) => schema.required('Data limite é obrigatório'),
        }),
    }),

    enableReinitialize: true, // Manter valores atualizados
    onSubmit: async (values) => {
      setIsLoading(true);

      // Certificar que os valores estão no formato correto
      const sanitizedValues = {
        ...values,
        available_value_transference: moneyMask(
          values.available_value_transference,
        ),
        lock_remaining: moneyMask(values.lock_remaining),
      };

      const { success, message } = await updateAjudTransference(
        ajudValue?.id || '',
        sanitizedValues,
      );

      if (success) {
        toast.success('Transferência atualizada com sucesso');
        handleClose(true);
      } else {
        toast.error(`Erro ao atualizar a transferência: ${message}`);
      }

      setIsLoading(false);
    },
  });

  useEffect(() => {
    // Verifica se o campo response_code contém os valores '07' ou '47'
    if (
      formik.values.response_code === '07' ||
      formik.values.response_code === '47'
    ) {
      // Se a condição for atendida, desabilita os campos de input
      setInputDisable(true);

      // Define os valores dos campos lock_remaining e available_value_transference como '0'
      // Isso garante que os campos sejam zerados quando o código de resposta for '07' ou '47'
      formik.setFieldValue('lock_remaining', '0');
      formik.setFieldValue('available_value_transference', '0');
    } else {
      // Caso contrário, habilita os campos de input
      setInputDisable(false);
    }
  }, [formik.values.response_code, formik.setFieldValue]);

  return (
    <Modal
      open={open}
      onClose={() => handleClose(true)}
      sx={{
        transform: 'translateZ(0)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box sx={style} component="form" onSubmit={formik.handleSubmit}>
        <Header
          title={`Detalhes do AJUD: ${ajudValue?.response_code == null ? '' : ajudValue?.response_code}`}
          subtitle={ajudValue?.court_order}
          label="TRANSFERÊNCIA"
          labelColor="#1976D2"
        />
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              marginTop: 1,
            }}
          >
            {ajudValue?.date_protocolization && (
              <InfoRow
                label="Data protocolização:"
                value={
                  ajudValue?.date_protocolization
                    ? moment(
                        ajudValue.date_protocolization.slice(0, 8),
                        'YYYYMMDD',
                      ).format('DD/MM/yyyy')
                    : ''
                }
              />
            )}
            {ajudValue?.transference_id && (
              <InfoRow
                label="ID da transferência:"
                value={ajudValue?.transference_id || ''}
              />
            )}

            {ajudValue?.agency_receiver && (
              <InfoRow
                label="Agência:"
                value={ajudValue?.agency_receiver || ''}
              />
            )}

            {ajudValue?.cnpj_inst_receiver && (
              <InfoRow
                label="CPF ou CNPJ da instituição:"
                value={ajudValue?.cnpj_inst_receiver || ''}
              />
            )}
            {ajudValue?.document_receiver && (
              <InfoRow
                label="Documento recebedor:"
                value={ajudValue?.document_receiver || ''}
              />
            )}

            {ajudValue?.name_receiver && (
              <InfoRow
                label="Nome recebedor:"
                value={ajudValue?.name_receiver || ''}
              />
            )}
            {ajudValue?.value_transference && (
              <InfoRow
                label="Valor de transferência:"
                value={ajudValue?.value_transference || ''}
              />
            )}
            {ajudValue?.lock_remaining_total && (
              <InfoRow
                label="Situação:"
                value={
                  ajudValue?.lock_remaining_total === '0'
                    ? 'Manter bloqueado'
                    : 'Encerrar bloqueio'
                }
              />
            )}
          </Box>
          {data?.data && data.data.length > 0 && (
            <TableContainer>
              <Table size="small" aria-label="a dense table">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: 16, fontWeight: 700 }}>
                      Data de Envio
                    </TableCell>
                    <TableCell sx={{ fontSize: 16, fontWeight: 700 }}>
                      Ordem
                    </TableCell>
                    <TableCell sx={{ fontSize: 16, fontWeight: 700 }}>
                      Documento
                    </TableCell>
                    <TableCell sx={{ fontSize: 16, fontWeight: 700 }}>
                      Valor Bloqueado
                    </TableCell>
                    <TableCell sx={{ fontSize: 16, fontWeight: 700 }}>
                      Status Ordem
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(
                    data?.data.slice(
                      page * rowsPerPage,
                      page * rowsPerPage + rowsPerPage,
                    ) || []
                  ).map((item, key) => (
                    <TableRow key={key}>
                      <TableCell>
                        {moment(item.shipping_date, 'DDMMYYYY').format(
                          'DD/MM/yyyy',
                        )}
                      </TableCell>
                      <TableCell>{item.court_order}</TableCell>
                      <TableCell>{item.cnpj_cpf_oj}</TableCell>
                      <TableCell>
                        {moneyMask(item.account_value_available)}
                      </TableCell>
                      <TableCell>
                        {item.canceled_order === 'T' ? (
                          <Typography color="red" fontSize={14}>
                            Ordem cancelada
                          </Typography>
                        ) : item.canceled_order === 'F' ? (
                          'Ordem reiterada'
                        ) : (
                          ''
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={6} />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5]}
                component="div"
                count={data?.data.length || 0}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
              />
            </TableContainer>
          )}
        </Box>
        <Typography variant="h6" sx={{ marginBottom: 2 }}>
          Atualize as informações de transferência
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
          <FormControl
            fullWidth
            error={Boolean(
              formik.errors.response_code && formik.touched.response_code,
            )}
          >
            <InputLabel id="response-code-label">Código resposta</InputLabel>
            <Select
              label="Código resposta"
              labelId="response-code-label"
              name="response_code"
              value={formik.values.response_code}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={isLoading}
              error={
                formik.touched.response_code &&
                Boolean(formik.errors.response_code)
              }
            >
              <MenuItem value="01">01 - Cumprida integralmente</MenuItem>
              <MenuItem value="07">
                07 - Operação já realizada fora do sistema Sisbajud
              </MenuItem>
              <MenuItem value="11">
                11 - Banco/agência destinatário da transferência inválido
              </MenuItem>
              <MenuItem value="47">
                47 - Operação a ser realizada fora do sistema Sisbajud. O Juízo
                será comunicado em até 30 dias
              </MenuItem>
            </Select>
            {formik.touched.response_code && formik.errors.response_code && (
              <FormHelperText sx={{ display: 'block', visibility: 'none' }}>
                {formik.errors.response_code}
              </FormHelperText>
            )}
          </FormControl>
          <DatePickerComponent
            label="Data limite"
            value={
              formik.values.data_limit_transference
                ? dayjs(formik.values.data_limit_transference)
                : null
            }
            onChange={(newValue) =>
              formik.setFieldValue('data_limit_transference', newValue)
            }
            error={formik.errors.data_limit_transference}
            touched={formik.touched.data_limit_transference}
          />
          <InputField
            id="available_value_transference"
            name="available_value_transference"
            label="Valor disponível"
            value={moneyMask(formik.values.available_value_transference)}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.errors.available_value_transference}
            touched={formik.touched.available_value_transference}
            disabled={isLoading || inputDisable}
          />
          <InputField
            id="lock_remaining"
            name="lock_remaining"
            label="Valor restante"
            value={moneyMask(formik.values.lock_remaining)}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.errors.lock_remaining}
            touched={formik.touched.lock_remaining}
            disabled={isLoading || inputDisable}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: 2,
            bottom: 16,
            right: 16,
          }}
        >
          {ajudValue?.status != 'DONE' && (
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isLoading || ajudValue?.status == 'DONE'}
              sx={{
                fontSize: {
                  xs: '0.75rem',
                  sm: '0.875rem',
                  marginLeft: 2,
                },
              }}
              startIcon={
                isLoading && <CircularProgress size={20} color="inherit" />
              } // Ícone de carregamento
            >
              {isLoading ? 'Atualizando...' : 'Atualizar informações'}
            </Button>
          )}
          <Button
            sx={{ marginLeft: 2, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
            variant="contained"
            color="error"
            onClick={() => handleClose()}
          >
            Cancelar
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default ModalTransfer;
